import { COLOR_PALETTE, NEUTRAL_COLOR } from '../../../shared/config/color-palette.js';

export type SpendingEntry = {
  key: string;
  label: string;
  color: string | null;
  exactMinor: number;
  estimatedMinor: number;
};

export type SpendingSlice = {
  key: string;
  label: string;
  amountMinor: number;
  share: number;
  color: string;
  isApproximate: boolean;
};

export type SpendingBreakdown = {
  totalMinor: number;
  slices: SpendingSlice[];
  colorByKey: Map<string, string>;
};

const MAX_OWN_SLICES = 5;

const OTHER_KEY = 'other';

const OTHER_LABEL = 'Інше';

export const spentMinorOf = (entry: { exactMinor: number; estimatedMinor: number }): number =>
  entry.exactMinor + entry.estimatedMinor;

const freeColorsFor = (entries: SpendingEntry[]): string[] => {
  const takenColors = new Set(entries.map((entry) => entry.color?.toLowerCase()));
  const freeColors = COLOR_PALETTE.filter(
    (color) => color !== NEUTRAL_COLOR && !takenColors.has(color),
  );

  return freeColors.length === 0 ? COLOR_PALETTE : freeColors;
};

const toOwnSlices = (entries: SpendingEntry[], totalMinor: number): SpendingSlice[] => {
  const freeColors = freeColorsFor(entries);
  const uncoloured = entries.filter((entry) => entry.color === null);

  return entries.map((entry) => {
    const freeColor = freeColors[uncoloured.indexOf(entry) % freeColors.length] ?? NEUTRAL_COLOR;

    return {
      key: entry.key,
      label: entry.label,
      amountMinor: spentMinorOf(entry),
      share: spentMinorOf(entry) / totalMinor,
      color: entry.color ?? freeColor,
      isApproximate: entry.estimatedMinor > 0,
    };
  });
};

const toOtherSlice = (entries: SpendingEntry[], totalMinor: number): SpendingSlice => {
  const amountMinor = entries.reduce((sum, entry) => sum + spentMinorOf(entry), 0);

  return {
    key: OTHER_KEY,
    label: OTHER_LABEL,
    amountMinor,
    share: amountMinor / totalMinor,
    color: NEUTRAL_COLOR,
    isApproximate: entries.some((entry) => entry.estimatedMinor > 0),
  };
};

export const byLargestSpending = (
  left: { exactMinor: number; estimatedMinor: number },
  right: { exactMinor: number; estimatedMinor: number },
): number => spentMinorOf(right) - spentMinorOf(left);

export const toSpendingBreakdown = (entries: SpendingEntry[]): SpendingBreakdown => {
  const spentEntries = entries
    .filter((entry) => spentMinorOf(entry) > 0)
    .toSorted(byLargestSpending);
  const totalMinor = spentEntries.reduce((sum, entry) => sum + spentMinorOf(entry), 0);
  const needsOther = spentEntries.length > MAX_OWN_SLICES + 1;
  const ownEntries = needsOther ? spentEntries.slice(0, MAX_OWN_SLICES) : spentEntries;
  const otherEntries = needsOther ? spentEntries.slice(MAX_OWN_SLICES) : [];
  const ownSlices = toOwnSlices(ownEntries, totalMinor);
  const slices = needsOther ? [...ownSlices, toOtherSlice(otherEntries, totalMinor)] : ownSlices;
  const colorByKey = new Map([
    ...ownSlices.map((slice) => [slice.key, slice.color] as const),
    ...otherEntries.map((entry) => [entry.key, NEUTRAL_COLOR] as const),
  ]);

  return { totalMinor, slices, colorByKey };
};
